/* @flow */

import { produce } from 'immer';

import type { AVENUE_DATA_MODAL_TYPE } from '../../../components/modal/avenueModal/AvenueModal';
import type { CARD_DATA_MODAL_TYPE } from '../../../components/modal/cardModal/CardModalConstsAndTypes';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../../../components/modal/confirmationModal/ConfirmationModal';
import type { EULA_DATA_MODAL_TYPE } from '../../../components/modal/eulaModal/EulaModal';
import type { GAME_PROFILE_SELECT_DATA_MODAL_TYPE } from '../../../components/modal/gameProfileSelectModal/GameProfileSelectModal';
import type { MESSAGE_MODAL_TYPE } from '../../../components/modal/messageModal/MessageModal';
import type { NEW_VERSION_DATA_MODAL_TYPE } from '../../../components/modal/newVersionModal/NewVersionModal';
import type { NPVR_DATA_MODAL_TYPE } from '../../../components/modal/npvrModal/NpvrModal';
import type { PIN_DATA_MODAL_TYPE } from '../../../components/modal/pinModal/PinModalConstsAndTypes';
import type { PRESENCE_CHECK_DATA_MODAL_TYPE } from '../../../components/modal/presenceCheckModal/PresenceCheckModal';
import type { ModalAction } from '../actions';

import {
  AUTHENTICATION_REQUIRED_MODAL,
  AVENUE_MODAL,
  CARD_MODAL,
  CONFIRMATION_MODAL,
  EULA_MODAL,
  EXTERNAL_CONTENT_MODAL,
  GAME_PROFILE_SELECT_MODAL,
  KEYBOARD_SHORTCUTS_MODAL,
  LEGAL_NOTICE_MODAL,
  MESSAGE_MODAL,
  type MODAL_TYPE,
  NEW_VERSION_MODAL,
  NPVR_HELP_MODAL,
  NPVR_MODAL,
  PARENTAL_CONTROL_MODAL,
  PIN_MODAL,
  PRESENCE_CHECK_MODAL,
  PROFILES_MODAL,
  PROFILE_AVATAR_SELECT_MODAL,
  PROFILE_EDITOR_MODAL,
  PROFILE_SPLASHSCREEN_MODAL,
  SETTINGS_MODAL,
  VOD_PURCHASE_MODAL,
} from '../../../components/modal/modalTypes';
import { type VOD_PURCHASE_DATA_TYPE, clearPendingOperationFromCard } from '../../../helpers/rights/pendingOperations';
import { ModalActionType } from '../constants';

export type ModalState = {
  ariaLabel?: string,
  avenueData?: AVENUE_DATA_MODAL_TYPE,
  cardData?: CARD_DATA_MODAL_TYPE,
  confirmationData?: CONFIRMATION_DATA_MODAL_TYPE,
  eulaData?: EULA_DATA_MODAL_TYPE,
  externalContentUrl?: string,
  index?: number,
  initialAvatarId?: string | null,
  gameProfileSelectData?: GAME_PROFILE_SELECT_DATA_MODAL_TYPE,
  messageData?: MESSAGE_MODAL_TYPE,
  modalId?: string,
  modalType?: MODAL_TYPE,
  newVersionData?: NEW_VERSION_DATA_MODAL_TYPE,
  npvrData?: NPVR_DATA_MODAL_TYPE,
  pinData?: PIN_DATA_MODAL_TYPE,
  presenceCheckData?: PRESENCE_CHECK_DATA_MODAL_TYPE,
  profileId?: string,
  selectedAvatarId?: string | null,
  vodPurchaseData?: VOD_PURCHASE_DATA_TYPE,
};

export type AllModalsState = {
  isEnabled: boolean,
  openModals: Array<ModalState>,
  savedModalCard: ?ModalState,
};

const InitialState: AllModalsState = {
  isEnabled: true,
  openModals: [],
  savedModalCard: null,
};

const DefaultAction: ModalAction = { type: null };

// eslint-disable-next-line complexity
const modalReducer: (state: AllModalsState, action: ModalAction) => AllModalsState = produce((draft = InitialState, action = DefaultAction) => {
  const { length: openModalCount } = draft.openModals;

  if (action.type === null) {
    return;
  }

  switch (action.type) {
    case ModalActionType.Disable:
      draft.isEnabled = false;
      return;

    case ModalActionType.Enable:
      draft.isEnabled = true;
      return;

    case ModalActionType.Show: {
      let newModal: ModalState = {};

      switch (action.modalType) {
        case AVENUE_MODAL:
          newModal = {
            ariaLabel: action.ariaLabel,
            avenueData: action.avenueData,
            modalType: action.modalType,
          };
          break;

        case CARD_MODAL:
          newModal = {
            cardData: action.cardData,
            modalType: action.modalType,
          };
          break;

        case CONFIRMATION_MODAL:
          newModal = {
            ariaLabel: action.ariaLabel,
            confirmationData: action.confirmationData,
            modalId: action.modalId,
            modalType: action.modalType,
          };
          break;

        case EULA_MODAL:
          newModal = {
            eulaData: action.eulaData,
            modalType: action.modalType,
          };
          break;

        case EXTERNAL_CONTENT_MODAL:
          newModal = {
            ariaLabel: action.ariaLabel,
            externalContentUrl: action.externalContentUrl,
            modalType: action.modalType,
          };
          break;

        case GAME_PROFILE_SELECT_MODAL:
          newModal = {
            gameProfileSelectData: action.gameProfileSelectData,
            modalType: action.modalType,
          };
          break;

        case NEW_VERSION_MODAL:
          newModal = {
            modalType: action.modalType,
            newVersionData: action.newVersionData,
          };
          break;

        case NPVR_MODAL:
          newModal = {
            modalType: action.modalType,
            npvrData: action.npvrData,
          };
          break;

        case AUTHENTICATION_REQUIRED_MODAL:
          // Hide all modals since user will be sent to the login screen
          draft.openModals.length = 0;

          newModal = {
            modalType: action.modalType,
          };
          break;

        case VOD_PURCHASE_MODAL:
          newModal = {
            modalType: action.modalType,
            vodPurchaseData: action.vodPurchaseData,
          };
          break;

        case PIN_MODAL:
          newModal = {
            modalId: action.modalId,
            modalType: action.modalType,
            pinData: action.pinData,
          };
          break;

        case KEYBOARD_SHORTCUTS_MODAL:
        case LEGAL_NOTICE_MODAL:
        case NPVR_HELP_MODAL:
        case PARENTAL_CONTROL_MODAL:
        case PROFILES_MODAL:
        case PROFILE_SPLASHSCREEN_MODAL:
        case SETTINGS_MODAL:
          newModal = { modalType: action.modalType };
          break;

        case PRESENCE_CHECK_MODAL:
          newModal = { modalType: action.modalType, presenceCheckData: action.presenceCheckData };
          break;

        case PROFILE_EDITOR_MODAL:
          newModal = { modalId: action.modalId, modalType: action.modalType, profileId: action.profileId };
          break;

        case PROFILE_AVATAR_SELECT_MODAL:
          newModal = { initialAvatarId: action.initialAvatarId, modalId: action.modalId, modalType: action.modalType, selectedAvatarId: action.selectedAvatarId };
          break;

        case MESSAGE_MODAL:
          newModal = {
            ariaLabel: action.ariaLabel,
            messageData: action.messageData,
            modalType: action.modalType,
          };
          break;

        default:
          return;
      }

      const sameModalIndex = draft.openModals.findIndex((m) => m.modalType === action.modalType);
      if (sameModalIndex > -1) {
        // Replace existing modal of same type
        draft.openModals[sameModalIndex] = newModal;
      } else {
        // Add new modal
        draft.openModals.push(newModal);
      }
      break;
    }

    case ModalActionType.Reopen: {
      if (draft.savedModalCard) {
        draft.openModals.push(draft.savedModalCard);
        draft.savedModalCard = null;
      }
      break;
    }

    case ModalActionType.SaveAndHide:
    case ModalActionType.Hide: {
      if (openModalCount === 0) {
        // Return entirely new draft
        return {
          ...InitialState,
          savedModalCard: draft.savedModalCard,
        };
      }

      if (action.force !== true && draft.openModals[openModalCount - 1].modalType === AUTHENTICATION_REQUIRED_MODAL) {
        // Do not close this kind of modal
        break;
      }

      // Remove top modal and store modal card if required
      const topModal = draft.openModals.pop();

      draft.savedModalCard ||= action.type === ModalActionType.SaveAndHide && topModal?.modalType === CARD_MODAL ? clearPendingOperationFromCard(topModal) : null;
      break;
    }

    // No default
  }
}, InitialState);

export default modalReducer;
